import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Animated,
  Platform,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native"

import type { YoutubeIframeRef } from "react-native-youtube-iframe"
import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { useIsFocused } from "@react-navigation/native"
import * as NavigationBar from "expo-navigation-bar"
import { useFocusEffect, useLocalSearchParams } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { setStatusBarHidden, StatusBar } from "expo-status-bar"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import YoutubePlayer from "react-native-youtube-iframe"

import type { YouTubeWebEmbedRef } from "src/components/YouTubeWebEmbed/types"
import { useWebsiteVideoActivity } from "src/components/ActivityTrackerWrapper"
import { SidekickView } from "src/components/SidekickView"
import { YouTubeWebEmbed } from "src/components/YouTubeWebEmbed"
import { useChat } from "src/context/ChatContext"
import { useMediaAvatar } from "src/context/MediaAvatarContext"
import { useMediaContext } from "src/context/MediaContextProvider"
import { useMicrophone } from "src/context/MicrophoneContext"
import { useSidekickInteraction } from "src/context/SidekickInteractionContext"
import { useVisemes } from "src/context/VisemeContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useStatusBarCompensation } from "src/hooks/useStatusBarCompensation"
import { WEB_CANVAS_MAX_WIDTH } from "src/hooks/useWebLayoutConstraints"
import { useWebMouseActivity } from "src/hooks/useWebMouseActivity"
import { backToRoot } from "src/utils/routerHelpers"
import { formatSecondsToTimestamp } from "src/utils/time"

// Extended ref type to include our patched methods
interface ExtendedYoutubeIframeRef extends YoutubeIframeRef {
  pauseVideo: () => void
  playVideo: () => void
}

export default function YouTubePlayerScreen() {
  const { videoId, startTime, resourceId } = useLocalSearchParams<{
    videoId?: string | string[]
    startTime?: string
    resourceId?: string
  }>()
  const resolvedVideoId = Array.isArray(videoId) ? videoId[0] : videoId
  const resolvedStartTime = startTime ? parseInt(startTime, 10) : 0
  const resolvedResourceId = Array.isArray(resourceId) ? resourceId[0] : resourceId
  const insets = useSafeAreaInsets()
  const { effectiveTopInset } = useStatusBarCompensation()
  const {
    theme: { colors },
    navTheme,
  } = useAppTheme()
  const { width, height } = useWindowDimensions()
  const isLandscape = width > height
  // True only while this screen is the focused/top route. Gates autoplay and
  // pauses playback if the screen is backgrounded, so a hidden player never
  // keeps playing audio.
  const isFocused = useIsFocused()
  const isLandscapeRef = useRef(isLandscape)
  useEffect(() => {
    isLandscapeRef.current = isLandscape
  }, [isLandscape])

  // Size the 16:9 player to fit entirely on screen (contain), like video-player, to avoid
  // bottom cropping in landscape when aspect-ratio would otherwise overflow height.
  // On web the app is constrained to a 720px centered canvas; useWindowDimensions() returns
  // the full browser viewport width, so we cap it here to prevent the player container from
  // overflowing the constrained layout and taking up the full browser window.
  const playerSize = useMemo(() => {
    const paddingHorizontal = 32 // matches playerStage paddingHorizontal: 16
    const canvasWidth = Platform.OS === "web" ? Math.min(width, WEB_CANVAS_MAX_WIDTH) : width
    const availableWidth = canvasWidth - paddingHorizontal
    const availableHeight = height
    const byWidth = { width: availableWidth, height: availableWidth * (9 / 16) }
    const byHeight = { width: availableHeight * (16 / 9), height: availableHeight }
    if (byWidth.height <= availableHeight) {
      return byWidth
    }
    return byHeight
  }, [width, height])

  const [isPlaying, setIsPlaying] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [isPlayerReady, setIsPlayerReady] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const hasAutoplayed = useRef(false)
  const controlsOpacity = useRef(new Animated.Value(1)).current

  const { isMouseActive, handleMouseMove } = useWebMouseActivity()
  // Show controls while paused, or on web when mouse/keyboard is active or typing.
  const shouldShowControls = !isPlaying || (Platform.OS === "web" && (isMouseActive || isTyping))
  const { setWebsiteVideoActivity } = useWebsiteVideoActivity()
  // Native player ref (react-native-youtube-iframe)
  const playerRef = useRef<ExtendedYoutubeIframeRef>(null)
  // Web player ref (YouTubeWebEmbed / react-youtube): getCurrentTime() is synchronous and returns
  // actual video time — unaffected by ads, which don't advance the video position.
  const webPlayerRef = useRef<YouTubeWebEmbedRef>(null)

  const { cancelMicStream } = useChat()
  const { isListening } = useMicrophone()
  const isListeningRef = useRef(isListening)
  useEffect(() => {
    isListeningRef.current = isListening
  }, [isListening])
  const { returnToIdle } = useSidekickInteraction()
  const { stopSpeech, isSpeaking } = useVisemes()
  const isSpeakingRef = useRef(isSpeaking)
  useEffect(() => {
    isSpeakingRef.current = isSpeaking
  }, [isSpeaking])
  const { setMediaContext, updateOffset, updateTotalLength, clearMediaContext } = useMediaContext()
  const { onMediaPlay, onMediaPause } = useMediaAvatar()

  // Register media context for YouTube videos.
  // Prefer the UUID resource_id from the OpenResource event; fall back to the YouTube URL.
  useEffect(() => {
    if (resolvedVideoId) {
      const videoUrl = `https://www.youtube.com/watch?v=${resolvedVideoId}`
      setMediaContext({
        resourceId: resolvedResourceId ?? videoUrl,
        initialOffset: formatSecondsToTimestamp(resolvedStartTime),
      })
    }
    return () => {
      clearMediaContext()
    }
  }, [resolvedVideoId, resolvedResourceId, resolvedStartTime, setMediaContext, clearMediaContext])

  useFocusEffect(
    useCallback(() => {
      return () => {
        console.log("YouTubePlayerScreen: Youtube player closed")
        setWebsiteVideoActivity(false)
      }
    }, [setWebsiteVideoActivity]),
  )

  // Unlock orientation for YouTube player
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.unlockAsync().catch(() => {})
      // Don't re-lock on cleanup to prevent jitter when auto-navigating between media
    }, []),
  )

  // Imperatively hide/show the status bar so we can chain the nav bar hide immediately after.
  // Hiding the status bar causes Android to show the nav bar — so we re-hide the nav bar
  // once the status bar slide animation (~300ms) has completed.
  // Showing the status bar is also deferred so it appears after the portrait rotation animation.
  useEffect(() => {
    if (Platform.OS !== "android") return
    if (isLandscape) {
      setStatusBarHidden(true, "fade")
      const timer = setTimeout(() => {
        NavigationBar.setVisibilityAsync("hidden")
        NavigationBar.setBehaviorAsync("overlay-swipe")
      }, 500)
      return () => clearTimeout(timer)
    } else {
      setStatusBarHidden(false, "fade")
      return () => {}
    }
  }, [isLandscape])

  // Fetch the video duration and update the media context total_length.
  // Called once the player has loaded enough metadata to know the duration.
  const fetchAndSetDuration = useCallback(async () => {
    try {
      const duration =
        Platform.OS === "web"
          ? (webPlayerRef.current?.getDuration() ?? 0)
          : ((await playerRef.current?.getDuration()) ?? 0)
      if (duration > 0) {
        updateTotalLength(formatSecondsToTimestamp(duration))
      }
    } catch {
      // Ignore — duration will simply be absent from media_context_info
    }
  }, [updateTotalLength])

  const handleVideoPlaying = useCallback(
    async (nowPlaying: boolean) => {
      if (nowPlaying) {
        // If the microphone is already listening, cancel the request and let the video play.
        // The isListening effect only fires on changes, so we guard here for the case
        // where the user manually presses play inside the YouTube controls while listening.
        if (isListeningRef.current) {
          cancelMicStream()
          returnToIdle()
        }
        // Guard: the YouTube IFrame API sends state-change postMessages asynchronously.
        // When isSpeaking causes us to call pauseVideo(), the iframe can fire a "playing"
        // state change back to the parent just before it processes the pause — arriving
        // here and calling stopSpeech() would clear the audio queue and silence the
        // sidekick. Skip stopSpeech() when we intentionally paused due to isSpeaking.
        if (!isSpeakingRef.current) {
          await stopSpeech()
        }
        onMediaPlay()
        // Fetch duration on first play — it's reliably available once playback starts
        fetchAndSetDuration()
      } else {
        onMediaPause()
      }
      setIsPlaying(nowPlaying)
      setWebsiteVideoActivity(nowPlaying)
    },
    [
      cancelMicStream,
      returnToIdle,
      setWebsiteVideoActivity,
      setIsPlaying,
      stopSpeech,
      onMediaPlay,
      onMediaPause,
      fetchAndSetDuration,
    ],
  )

  // Called when the player enters the buffering state (state 3). Buffering fires right before an
  // ad or the main video starts playing. If nothing has played yet (!isPlaying) it almost certainly
  // means a pre-roll ad or the video itself is about to start — cancel the mic request now so
  // audio isn't captured, even for ad types that never advance to state 1 ("playing").
  const handleVideoBuffering = useCallback(() => {
    if (isListeningRef.current && !isPlaying) {
      cancelMicStream()
      returnToIdle()
    }
  }, [cancelMicStream, returnToIdle, isPlaying])

  // Also re-run when isPlayerReady changes: YouTube's IFrame player can autoplay on its own
  // (the WebView allows media playback without a user gesture), so we must re-check and pause
  // when the player first becomes ready while the microphone is already listening.
  useEffect(() => {
    if (!isListening || !isPlayerReady) return
    if (Platform.OS === "web") {
      webPlayerRef.current?.pauseVideo()
    } else {
      playerRef.current?.pauseVideo()
    }
    setIsPlaying(false)
    setWebsiteVideoActivity(false)
  }, [isListening, isPlayerReady, setWebsiteVideoActivity, setIsPlaying])

  // Animate controls opacity: fade out when hidden, fade in when visible.
  // On web this reacts to both playback state and mouse activity.
  useEffect(() => {
    Animated.timing(controlsOpacity, {
      toValue: shouldShowControls ? 1 : 0,
      duration: 300,
      useNativeDriver: true,
    }).start()
  }, [shouldShowControls, controlsOpacity])

  // Poll getCurrentTime() every 5s while playing to keep the media context offset current.
  // On web: react-youtube provides the YouTube IFrame API player; getCurrentTime() is synchronous
  // and returns actual video time (unaffected by ads, which don't advance getCurrentTime()).
  // On native: react-native-youtube-iframe's getCurrentTime() is async via injectJavaScript.
  useEffect(() => {
    if (!isPlaying) return
    const interval = setInterval(
      async () => {
        try {
          const currentTime =
            Platform.OS === "web"
              ? (webPlayerRef.current?.getCurrentTime() ?? undefined)
              : await playerRef.current?.getCurrentTime()
          if (currentTime !== undefined) {
            updateOffset(formatSecondsToTimestamp(currentTime))
          }
        } catch {
          // Ignore transient errors
        }
      },
      Platform.OS === "web" ? 2000 : 5000,
    )
    return () => clearInterval(interval)
  }, [isPlaying, updateOffset])

  // Autoplay once the player is ready and the sidekick has finished speaking.
  // Do not autoplay or seek while the user is listening (Talk) — YouTube can start playback on seek,
  // and we must avoid the mic capturing video audio.
  // Seek here rather than in onReady — calling seekTo there can fire onChangeState("playing")
  // asynchronously via the WebView bridge, bypassing the isSpeaking guard and cutting off speech.
  useEffect(() => {
    if (!isPlayerReady || isSpeaking || isListening || hasAutoplayed.current) return
    // Don't autoplay unless this screen is actually on top. If several media
    // events arrived together (or another player was pushed over this one),
    // this instance can be mounted but hidden — autoplaying here would produce
    // audible playback from a video that isn't visible.
    if (!isFocused) return
    hasAutoplayed.current = true
    if (Platform.OS === "web") {
      if (resolvedStartTime > 0) {
        webPlayerRef.current?.seekTo(resolvedStartTime)
      }
      webPlayerRef.current?.playVideo()
    } else {
      if (resolvedStartTime > 0) {
        playerRef.current?.seekTo(resolvedStartTime, true)
      }
      playerRef.current?.playVideo()
    }
  }, [isPlayerReady, isSpeaking, isListening, isFocused, resolvedStartTime])

  // Pause whenever the screen loses focus (another screen pushed on top, app
  // backgrounded, etc.) so a hidden player never keeps playing audio.
  useEffect(() => {
    if (isFocused) return
    if (Platform.OS === "web") {
      webPlayerRef.current?.pauseVideo()
    } else {
      playerRef.current?.pauseVideo()
    }
    setIsPlaying(false)
    setWebsiteVideoActivity(false)
  }, [isFocused, setWebsiteVideoActivity])

  // Belt-and-suspenders: guarantee playback stops if this screen unmounts (e.g.
  // popped back to chat) so audio never outlives the visible player.
  useEffect(() => {
    return () => {
      try {
        if (Platform.OS === "web") {
          webPlayerRef.current?.pauseVideo()
        } else {
          playerRef.current?.pauseVideo()
        }
      } catch {
        // Player may already be torn down; ignore.
      }
    }
  }, [])

  // Pause when the sidekick starts speaking so its TTS reply is audible.
  // On Android, expo-av's DoNotMix audio mode means the playing video holds
  // exclusive audio focus — the sidekick's TTS chunks stay queued (silent)
  // until the player releases focus.
  useEffect(() => {
    if (!isSpeaking) return
    if (Platform.OS === "web") {
      webPlayerRef.current?.pauseVideo()
    } else {
      playerRef.current?.pauseVideo()
    }
    setIsPlaying(false)
    setWebsiteVideoActivity(false)
  }, [isSpeaking, setWebsiteVideoActivity])

  // Handle mode changes from SidekickView
  const handleModeChange = useCallback(
    (mode: "idle" | "talk" | "type") => {
      setIsTyping(mode === "type")
      if (mode === "talk" || mode === "type") {
        // Pause immediately so audio focus is released before the sidekick's
        // TTS chunks arrive (same race-condition fix as video-player).
        if (Platform.OS === "web") {
          webPlayerRef.current?.pauseVideo()
        } else {
          playerRef.current?.pauseVideo()
        }
        setIsPlaying(false)
        setWebsiteVideoActivity(false)
      }
    },
    [setIsPlaying, setWebsiteVideoActivity],
  )

  if (!resolvedVideoId) {
    return <View style={styles.container} />
  }

  return (
    <View style={styles.container} {...({ onMouseMove: handleMouseMove } as any)}>
      <StatusBar style={navTheme.dark ? "light" : "dark"} />
      {/* Close button - above SidekickView */}
      {/* Use fixed position based on status bar height to avoid jump when status bar hides */}
      <Animated.View
        style={[
          styles.closeButton,
          {
            top: effectiveTopInset + 22,
            right: insets.right + 12,
            opacity: controlsOpacity,
          },
          Platform.OS === "web" && ({ position: "fixed" } as any),
        ]}
        pointerEvents={"auto"}
      >
        <TouchableOpacity
          onPress={async () => {
            // Unmount AvatarOverlay if in landscape to prevent crash during orientation change (APP-738)
            if (isLandscape) {
              setIsClosing(true)
              // Wait for React to unmount the component
              await new Promise((resolve) => setTimeout(resolve, 0))
            }
            backToRoot()
          }}
          style={[styles.closeButtonInner, { backgroundColor: colors.buttonBackground }]}
          accessibilityRole="button"
          accessibilityLabel="Close YouTube player"
        >
          <MaterialIcons name="close" size={20} color={colors.buttonText} />
        </TouchableOpacity>
      </Animated.View>

      {!isClosing && (
        <SidekickView
          showAvatar
          autoShowAvatarOnSpeech
          showCaptions
          showCCToggle
          showButtonCluster
          compactButtonCluster
          showControlsBarBackground={false}
          controlsOpacity={controlsOpacity}
          controlsVisible={shouldShowControls}
          onModeChange={handleModeChange}
          riveOwner="video"
          controlsConfig={{ type: "none" }}
        >
          <View style={styles.playerStage}>
            <View
              style={[
                styles.playerContainer,
                { width: playerSize.width, height: playerSize.height },
              ]}
            >
              {Platform.OS === "web" && YouTubeWebEmbed ? (
                // On web: react-native-youtube-iframe's postMessage bridge is broken
                // (relies on window.ReactNativeWebView which doesn't exist in browsers).
                // YouTubeWebEmbed uses the YouTube IFrame API directly — real callbacks,
                // synchronous getCurrentTime() that reflects actual video position (not ads).
                <YouTubeWebEmbed
                  ref={webPlayerRef}
                  videoId={resolvedVideoId}
                  startTime={resolvedStartTime}
                  onReady={() => setIsPlayerReady(true)}
                  onPlay={() => handleVideoPlaying(true)}
                  onPause={() => handleVideoPlaying(false)}
                  onEnd={() => handleVideoPlaying(false)}
                  onError={() => handleVideoPlaying(false)}
                  onStateChange={(state: number) => {
                    if (state === 3 /* buffering */) {
                      handleVideoBuffering()
                    }
                  }}
                />
              ) : (
                <YoutubePlayer
                  ref={playerRef}
                  height="100%"
                  width="100%"
                  videoId={resolvedVideoId}
                  play={isPlaying}
                  volume={50}
                  playbackRate={1}
                  initialPlayerParams={{
                    controls: true,
                    cc_lang_pref: "us",
                    iv_load_policy: 3,
                    showClosedCaptions: false,
                    preventFullScreen: false,
                    rel: false,
                    // Best-effort reduction of YouTube branding/metadata chrome
                    modestbranding: 1,
                    showinfo: 0,
                    start: resolvedStartTime,
                  }}
                  onFullScreenChange={(isFullScreen: boolean) => {
                    // After exiting the player's fullscreen mode, re-unlock orientation so the screen
                    // can rotate again (some platforms/players leave it locked).
                    if (!isFullScreen) {
                      ScreenOrientation.unlockAsync().catch(() => {})
                    }
                  }}
                  onChangeState={(state: string) => {
                    if (state === "playing") {
                      handleVideoPlaying(true)
                    } else if (state === "paused" || state === "ended") {
                      handleVideoPlaying(false)
                    } else if (state === "buffering") {
                      handleVideoBuffering()
                    }
                  }}
                  onReady={() => {
                    // Brief delay allows the player to fully initialize and reduces flashing.
                    // Do NOT seekTo here — it can fire onChangeState("playing") via the WebView
                    // bridge before isSpeaking is checked, cutting off the sidekick's speech.
                    // The seek is deferred to the autoplay effect below.
                    setTimeout(() => {
                      fetchAndSetDuration()
                      setIsPlayerReady(true)
                    }, 1000)
                  }}
                  onError={(error: any) => {
                    console.error("YouTubePlayerScreen: Youtube player error -", error)
                    handleVideoPlaying(false)
                  }}
                />
              )}
            </View>
          </View>
        </SidekickView>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#000000",
  },
  closeButton: {
    position: "absolute",
    zIndex: 2100,
  },
  closeButtonInner: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
  },
  // Full-screen stage that centers the 16:9 player container (size applied inline to fit screen)
  playerStage: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 16,
  },
  playerContainer: {
    // width/height set inline from playerSize so video fits entirely (contain) in all orientations
  },
})
